import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Sidebar } from '../../shared/sidebar/sidebar';
import { AuditLogService, AuditLogEntry, PaginatedAuditLogs } from '../../core/services/audit-log.service';

@Component({
  selector: 'app-activity-log',
  imports: [
    CommonModule, FormsModule, Sidebar,
    MatFormFieldModule, MatSelectModule, MatInputModule,
    MatDatepickerModule, MatNativeDateModule,
    MatButtonModule, MatTableModule, MatPaginatorModule, MatProgressSpinnerModule
  ],
  templateUrl: './activity-log.html',
  styleUrl: './activity-log.scss',
})
export class ActivityLog implements OnInit {
  logs: AuditLogEntry[] = [];
  isLoading = false;
  pageSize = 50;
  totalCount = 0;
  pageIndex = 0;

  // Filters
  filterEntityType = '';
  filterAction = '';
  filterFrom: Date | null = null;
  filterTo: Date | null = null;

  displayedColumns = ['date', 'user', 'action', 'type', 'details'];

  entityTypes = ['Tournament', 'TeamRegistration', 'IndividualRegistration', 'Match', 'SessionPlan', 'UserSessionPurchase', 'SessionAttendance', 'User'];
  actions = ['CreateTournament', 'UpdateTournament', 'RegisterTeam', 'RegisterIndividual', 'CreateTeamFromIndividuals', 'RecordPayment', 'CreateMatch', 'UpdateMatch', 'DeleteMatch', 'CreatePlan', 'UpdatePlan', 'DeletePlan', 'RecordAttendance', 'UndoAttendance', 'CreateCoach'];

  constructor(private auditLogService: AuditLogService) {}

  ngOnInit() {
    this.loadLogs();
  }

  loadLogs() {
    this.isLoading = true;
    const fromStr = this.filterFrom ? this.formatDateParam(this.filterFrom) : undefined;
    const toStr = this.filterTo ? this.formatDateParam(this.filterTo) : undefined;
    this.auditLogService.getLogs(
      this.pageIndex + 1,
      this.pageSize,
      undefined,
      this.filterEntityType || undefined,
      this.filterAction || undefined,
      fromStr,
      toStr
    ).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.logs = response.data.logs;
          this.totalCount = response.data.totalCount;
          this.pageIndex = response.data.currentPage - 1;
        }
        this.isLoading = false;
      },
      error: () => { this.isLoading = false; }
    });
  }

  applyFilters() {
    this.pageIndex = 0;
    this.loadLogs();
  }

  clearFilters() {
    this.filterEntityType = '';
    this.filterAction = '';
    this.filterFrom = null;
    this.filterTo = null;
    this.pageIndex = 0;
    this.loadLogs();
  }

  onPage(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadLogs();
  }

  get hasActiveFilters(): boolean {
    return !!(this.filterEntityType || this.filterAction || this.filterFrom || this.filterTo);
  }

  getActionBadgeClass(action: string): string {
    if (action.startsWith('Create') || action.startsWith('Register')) return 'badge-create';
    if (action.startsWith('Update')) return 'badge-update';
    if (action.startsWith('Delete') || action.startsWith('Undo')) return 'badge-delete';
    if (action.startsWith('Record')) return 'badge-record';
    return '';
  }

  formatAction(action: string): string {
    return action.replace(/([A-Z])/g, ' $1').trim();
  }

  formatDate(dateStr: string): string {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
      + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  }

  private formatDateParam(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
}
